import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Loader2, Globe, ArrowLeft } from 'lucide-react';

export default function ForgotPassword() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');

    const [successEmail, setSuccessEmail] = useState('');

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!email) {
            setError(t('auth.invalidEmail'));
            return;
        }

        setLoading(true);
        setError('');
        setSuccessEmail('');

        try {
            console.log("Sending forgot password request for email:", email, "lang:", i18n.language);
            const response = await authAPI.forgotPassword({ email, lang: i18n.language });
            setSuccessEmail(response.data.email);
            // Toast removed as requested
        } catch (err) {
            if (err.response?.status === 404) {
                setError('auth.userNotFound');
            } else {
                setError(err.response?.data?.detail || 'common.operationFailed');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-[#f0f2f5] p-4 relative">
            <div className="absolute top-4 right-4">
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="rounded-lg">
                            <Globe className="w-5 h-5 text-gray-600" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-white">
                        <DropdownMenuItem onClick={() => i18n.changeLanguage('tr')}>Türkçe</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => i18n.changeLanguage('en')}>English</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => i18n.changeLanguage('de')}>Deutsch</DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>

            {/* Header Section */}
            <div className="flex flex-col items-center mb-4">
                <div className="w-56 h-56 flex items-center justify-center -mb-8">
                    <img src="/logo.png" alt="Connected Mobile" className="w-full h-full object-contain" />
                </div>
                <h1 className="text-lg font-bold text-gray-900">{t('auth.brandName', { defaultValue: 'Contract Manager' })}</h1>
                <p className="text-gray-500 text-sm">www.connectedmobile.de</p>
            </div>

            {/* Forgot Password Card */}
            <Card className="w-full max-w-[400px] bg-white border-0 shadow-sm">
                <CardHeader className="space-y-1 pb-4">
                    <CardTitle className="text-2xl font-bold text-left">{t('auth.forgotPassword')}</CardTitle>
                    <CardDescription className="text-left text-gray-500">
                        {successEmail
                            ? t('auth.resetLinkSent', { defaultValue: 'Şifre sıfırlama bağlantısı gönderildi!' })
                            : t('auth.forgotPasswordSubtitle', { defaultValue: 'Şifrenizi sıfırlamak için e-posta adresinizi giriniz.' })
                        }
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {successEmail ? (
                        <div className="space-y-6">
                            <div className="p-4 bg-green-50 border border-green-100 rounded-lg">
                                <p className="text-sm text-green-700 font-medium text-center leading-relaxed">
                                    Email: <span className="font-bold">{successEmail}</span>
                                    <br />
                                    {t('auth.resetMailSentFeedback', { defaultValue: 'şifre sıfırlama maili gönderildi' })}
                                </p>
                            </div>
                            <div className="text-center">
                                <Link
                                    to="/login"
                                    className="text-sm font-medium text-gray-600 hover:text-black inline-flex items-center gap-2"
                                >
                                    <ArrowLeft className="w-4 h-4" />
                                    {t('auth.backToLogin', { defaultValue: 'Giriş Ekranına Dön' })}
                                </Link>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={onSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="email" className="text-gray-700 font-medium">
                                    {t('auth.email')}
                                </Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder={t('auth.emailPlaceholder')}
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="h-11 border-gray-200 focus:border-black focus:ring-black"
                                />
                                {error && (
                                    <p className="text-xs text-red-500 font-medium">
                                        {error.includes('.') ? t(error) : error}
                                    </p>
                                )}
                            </div>

                            <Button
                                type="submit"
                                className="w-full bg-[#1a1a1a] hover:bg-black text-white font-bold h-11 mt-2"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        {t('common.loading')}
                                    </>
                                ) : (
                                    t('common.send', { defaultValue: 'Gönder' })
                                )}
                            </Button>

                            <div className="text-center pt-2">
                                <Link
                                    to="/login"
                                    className="text-sm font-medium text-gray-600 hover:text-black inline-flex items-center gap-2"
                                >
                                    <ArrowLeft className="w-4 h-4" />
                                    {t('auth.backToLogin', { defaultValue: 'Giriş Ekranına Dön' })}
                                </Link>
                            </div>
                        </form>
                    )}
                </CardContent>
            </Card>

            {/* Footer */}
            <div className="mt-8 text-center text-xs text-gray-300 font-medium">
                {t('auth.brandFooter', { defaultValue: 'Hand crafted & made by ZeuZ-IT' })}
            </div>
        </div>
    );
}
